import { useEffect, useMemo, useState } from 'react';
import type { Incident } from '../data/demoData';
import { apiFetch, ApiError } from '../lib/apiClient';

interface AttackGraphNode {
  id: string;
  label: string;
  kind: 'incident' | 'alert' | 'entity' | 'device' | 'user' | 'host' | 'ip' | 'url' | 'file' | 'process' | 'mail' | 'bookmark' | 'relation' | 'other';
  source: string;
  severity?: string;
  status?: string;
  category?: string;
  description?: string;
  timestamp?: string;
}

interface AttackGraphEdge {
  id: string;
  from: string;
  to: string;
  label: string;
  source: string;
}

interface AttackTimelineItem {
  id: string;
  timestamp: string;
  title: string;
  kind: string;
  severity?: string;
  status?: string;
  source: string;
  detail?: string;
  entityRefs?: string[];
}

interface AttackStoryQuery {
  title: string;
  description: string;
  query: string;
}

interface IncidentAttackStory {
  incidentId: string;
  incidentNumber?: string;
  title?: string;
  provider?: string;
  fetchedAt: string;
  sources: { source: string; ok: boolean; message: string }[];
  nodes: AttackGraphNode[];
  edges: AttackGraphEdge[];
  timeline: AttackTimelineItem[];
  story: {
    summary: string;
    attackChain: string[];
    gaps: string[];
    nextActions: string[];
  };
  suggestedKqlQueries: AttackStoryQuery[];
  portalUrls: { sentinel?: string; defender?: string };
}

interface AttackStoryAiResult {
  analysis: string;
  model: string;
  usedAi: boolean;
  prompt: string;
}

interface ReplayStep {
  id: string;
  timestamp?: string;
  title: string;
  detail?: string;
  severity?: string;
  source?: string;
  kind?: string;
}

interface Props {
  incident: Incident;
  customerId: string;
  canAnalyze: boolean;
  canReplay?: boolean;
  onRunKql?: (query: string) => void;
}

const ATTACK_STORY_EXTRA_PROMPT = `Analyze this investigation graph as a Microsoft Sentinel / Defender XDR SOC lead. Focus on: 1) the likely attacker path and impacted blast radius, 2) whether the returned graph is sufficient to justify true-positive handling, 3) evidence gaps caused by missing Sentinel/Defender sources, 4) concrete KQL hunts to prove or disprove compromise across the scoped workspace, and 5) containment steps ranked by urgency. Do not invent telemetry; clearly separate confirmed facts from hypotheses.`;

function formatDate(value?: string): string {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function severityClass(value?: string): string {
  const v = (value || '').toLowerCase();
  if (v.includes('critical') || v.includes('high')) return 'bg-red-500/10 text-red-700 border-red-500/30 dark:text-red-300';
  if (v.includes('medium')) return 'bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300';
  if (v.includes('low')) return 'bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300';
  return 'bg-muted text-muted-foreground border-border';
}

function truncate(value: string, max = 42): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function nodeIcon(kind: AttackGraphNode['kind']): string {
  switch (kind) {
    case 'incident': return '🚨';
    case 'alert': return '⚠️';
    case 'device': return '💻';
    case 'host': return '🖥️';
    case 'user': return '👤';
    case 'ip': return '🌐';
    case 'url': return '🔗';
    case 'file': return '📄';
    case 'process': return '⚙️';
    case 'mail': return '✉️';
    case 'bookmark': return '🔖';
    default: return '•';
  }
}

function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.split(/\r?\n/);
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {lines.map((line, idx) => {
        if (line.startsWith('## ')) {
          return <h4 key={idx} className="pt-3 text-base font-semibold text-foreground">{line.replace(/^##\s+/, '')}</h4>;
        }
        if (line.startsWith('- ')) {
          return <div key={idx} className="ml-3 flex gap-2 text-muted-foreground"><span>•</span><span>{line.slice(2)}</span></div>;
        }
        if (/^\d+\.\s+/.test(line)) {
          return <div key={idx} className="ml-3 text-muted-foreground">{line}</div>;
        }
        if (!line.trim()) {
          return <div key={idx} className="h-1" />;
        }
        return <p key={idx} className="text-muted-foreground">{line}</p>;
      })}
    </div>
  );
}

function AttackGraphChart({ nodes, edges, onAnalyze, canAnalyze, analyzing }: { nodes: AttackGraphNode[]; edges: AttackGraphEdge[]; onAnalyze: () => void; canAnalyze: boolean; analyzing: boolean }) {
  const layout = useMemo(() => {
    const buckets: Record<string, AttackGraphNode[]> = { incident: [], alerts: [], entities: [] };
    for (const node of nodes.slice(0, 70)) {
      if (node.kind === 'incident') buckets.incident.push(node);
      else if (node.kind === 'alert' || node.kind === 'bookmark' || node.kind === 'relation') buckets.alerts.push(node);
      else buckets.entities.push(node);
    }
    const positions = new Map<string, { x: number; y: number; node: AttackGraphNode }>();
    const put = (items: AttackGraphNode[], x: number) => {
      const count = Math.max(1, items.length);
      items.forEach((node, i) => {
        const y = 70 + i * Math.max(62, Math.min(92, 620 / count));
        positions.set(node.id, { x, y, node });
      });
    };
    put(buckets.incident.length ? buckets.incident : nodes.slice(0, 1), 95);
    put(buckets.alerts, 380);
    put(buckets.entities, 690);
    const height = Math.max(360, Math.max(...Array.from(positions.values()).map(p => p.y), 300) + 70);
    const visibleEdges = edges.filter(e => positions.has(e.from) && positions.has(e.to)).slice(0, 110);
    return { positions, visibleEdges, height };
  }, [nodes, edges]);

  return (
    <div className="rounded-2xl border bg-card p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-semibold text-foreground">Attack Investigation Graph</h4>
          <p className="text-xs text-muted-foreground">Graph is reconstructed from available Sentinel, Defender XDR, Graph Security and workspace evidence.</p>
        </div>
        {canAnalyze && (
          <button
            type="button"
            onClick={onAnalyze}
            disabled={analyzing || nodes.length === 0}
            className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {analyzing ? 'Analyzing…' : '✨ AI Analysis'}
          </button>
        )}
      </div>
      <div className="overflow-auto rounded-xl border bg-muted/20">
        <svg width="940" height={layout.height} role="img" aria-label="Attack investigation graph">
          <defs>
            <marker id="attack-arrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill="currentColor" />
            </marker>
          </defs>
          <g className="text-border" stroke="currentColor" strokeWidth="1.4" fill="none" markerEnd="url(#attack-arrow)">
            {layout.visibleEdges.map(edge => {
              const from = layout.positions.get(edge.from)!;
              const to = layout.positions.get(edge.to)!;
              return <line key={edge.id} x1={from.x + 78} y1={from.y} x2={to.x - 88} y2={to.y} />;
            })}
          </g>
          <g className="text-muted-foreground" fontSize="10">
            {layout.visibleEdges.slice(0, 45).map(edge => {
              const from = layout.positions.get(edge.from)!;
              const to = layout.positions.get(edge.to)!;
              return <text key={`${edge.id}:label`} x={(from.x + to.x) / 2 - 34} y={(from.y + to.y) / 2 - 4}>{truncate(edge.label, 22)}</text>;
            })}
          </g>
          {[...layout.positions.values()].map(({ x, y, node }) => (
            <g key={node.id} transform={`translate(${x - 82}, ${y - 25})`}>
              <rect width="164" height="50" rx="12" className="fill-card stroke-border" strokeWidth="1" />
              <text x="12" y="20" className="fill-foreground" fontSize="13" fontWeight="600">{nodeIcon(node.kind)} {truncate(node.label, 25)}</text>
              <text x="12" y="37" className="fill-muted-foreground" fontSize="10">{node.kind}{node.severity ? ` • ${node.severity}` : ''}</text>
            </g>
          ))}
        </svg>
      </div>
      {nodes.length > 70 && <p className="mt-2 text-xs text-muted-foreground">Showing first 70 nodes to keep the chart readable. Use suggested KQL pivots for full telemetry review.</p>}
    </div>
  );
}

function AttackReplayPanel({ steps, activeIndex, playing, onPlayPause, onNext, onPrev, onClose, onSelect }: {
  steps: ReplayStep[];
  activeIndex: number;
  playing: boolean;
  onPlayPause: () => void;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  onSelect: (idx: number) => void;
}) {
  const active = steps[Math.min(Math.max(activeIndex, 0), Math.max(steps.length - 1, 0))];
  const pct = steps.length <= 1 ? 100 : Math.round((activeIndex / (steps.length - 1)) * 100);
  return (
    <div className="rounded-2xl border border-fuchsia-500/30 bg-fuchsia-500/5 p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold text-foreground">Attack Story Replay Mode</h4>
          <p className="text-xs text-muted-foreground">Step through the reconstructed investigation timeline like a movie. Use this for analyst handoff, PIR review, or customer walkthroughs.</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg border px-3 py-1.5 text-xs hover:bg-muted">Close Replay</button>
      </div>

      <div className="rounded-xl border bg-card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs font-medium text-muted-foreground">Step {Math.min(activeIndex + 1, steps.length)} of {steps.length}</div>
          <div className="flex gap-2">
            <button type="button" onClick={onPrev} disabled={activeIndex <= 0} className="rounded-lg border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">Previous</button>
            <button type="button" onClick={onPlayPause} className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-1.5 text-xs font-semibold text-fuchsia-700 hover:bg-fuchsia-500/15 dark:text-fuchsia-200">{playing ? 'Pause' : 'Play'}</button>
            <button type="button" onClick={onNext} disabled={activeIndex >= steps.length - 1} className="rounded-lg border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">Next</button>
          </div>
        </div>
        <div className="mb-4 h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-fuchsia-500 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        {active ? (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
            <div className="rounded-2xl border bg-background p-5">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full border bg-muted px-2.5 py-1 text-xs text-muted-foreground">{active.kind || 'timeline'}</span>
                {active.severity && <span className={`rounded-full border px-2.5 py-1 text-xs ${severityClass(active.severity)}`}>{active.severity}</span>}
                {active.source && <span className="rounded-full border bg-muted px-2.5 py-1 text-xs text-muted-foreground">{active.source}</span>}
              </div>
              <h5 className="text-base font-semibold text-foreground">{active.title}</h5>
              <p className="mt-1 text-xs text-muted-foreground">{formatDate(active.timestamp)}</p>
              {active.detail && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{active.detail}</p>}
            </div>
            <div className="rounded-2xl border bg-background p-3">
              <div className="mb-2 text-xs font-semibold text-foreground">Replay Queue</div>
              <div className="max-h-72 space-y-1 overflow-auto pr-1">
                {steps.map((step, idx) => (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => onSelect(idx)}
                    className={`w-full rounded-lg border px-2 py-2 text-left text-xs transition ${idx === activeIndex ? 'border-fuchsia-500/50 bg-fuchsia-500/10 text-foreground' : 'bg-card text-muted-foreground hover:bg-muted/50'}`}
                  >
                    <div className="font-medium">{idx + 1}. {truncate(step.title, 36)}</div>
                    <div className="mt-0.5 text-[10px] opacity-80">{formatDate(step.timestamp)}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : <p className="text-sm text-muted-foreground">No replayable timeline steps were returned for this incident.</p>}
      </div>
    </div>
  );
}

export function AttackStoryPanel({ incident, customerId, canAnalyze, canReplay = false, onRunKql }: Props) {
  const [story, setStory] = useState<IncidentAttackStory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AttackStoryAiResult | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [copiedQuery, setCopiedQuery] = useState<string | null>(null);
  const [replayOpen, setReplayOpen] = useState(false);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [replayAuditSent, setReplayAuditSent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (customerId) params.set('customerId', customerId);
    if (incident.incidentNumber) params.set('incidentNumber', incident.incidentNumber);
    if (incident.armIncidentName) params.set('armIncidentName', incident.armIncidentName);
    if (incident.graphIncidentId) params.set('graphIncidentId', incident.graphIncidentId);
    if (incident.title) params.set('title', incident.title);
    if (incident.providerName) params.set('providerName', incident.providerName);

    setLoading(true);
    setError(null);
    setAnalysis(null);
    setReplayOpen(false);
    setReplayPlaying(false);
    setReplayIndex(0);
    setReplayAuditSent(false);
    apiFetch<IncidentAttackStory>(`/incidents/${encodeURIComponent(incident.id)}/attack-story?${params.toString()}`)
      .then(data => { if (!cancelled) setStory(data); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.forbidden) setError('Your role does not have permission to view the attack story / investigation graph.');
        else setError(String(e instanceof Error ? e.message : e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, incident.id, incident.incidentNumber, incident.armIncidentName, incident.graphIncidentId, incident.title, incident.providerName]);

  const counts = useMemo(() => {
    const nodes = story?.nodes ?? [];
    return {
      alerts: nodes.filter(n => n.kind === 'alert').length,
      entities: nodes.filter(n => !['incident', 'alert', 'bookmark', 'relation'].includes(n.kind)).length,
      edges: story?.edges.length ?? 0,
      timeline: story?.timeline.length ?? 0,
    };
  }, [story]);


  const replaySteps = useMemo<ReplayStep[]>(() => {
    if (!story) return [];
    const timelineSteps = story.timeline.map((item, idx) => ({
      id: item.id || `timeline-${idx}`,
      timestamp: item.timestamp,
      title: item.title,
      detail: item.detail,
      severity: item.severity,
      source: item.source,
      kind: item.kind,
    }));
    if (timelineSteps.length > 0) return timelineSteps.slice(0, 80);
    return story.story.attackChain.map((line, idx) => ({
      id: `attack-chain-${idx}`,
      title: line.replace(/^\d{4}-\d{2}-\d{2}T[^:]+:\s*/, ''),
      detail: line,
      kind: 'story',
    })).slice(0, 50);
  }, [story]);

  useEffect(() => {
    if (!replayOpen || !replayPlaying || replaySteps.length === 0) return;
    const timer = window.setTimeout(() => {
      setReplayIndex((idx) => {
        if (idx >= replaySteps.length - 1) {
          setReplayPlaying(false);
          return idx;
        }
        return idx + 1;
      });
    }, 1700);
    return () => window.clearTimeout(timer);
  }, [replayOpen, replayPlaying, replayIndex, replaySteps.length]);

  const startReplay = async () => {
    if (!canReplay || replaySteps.length === 0) return;
    setReplayOpen(true);
    setReplayPlaying(true);
    setReplayIndex(0);
    if (!replayAuditSent) {
      setReplayAuditSent(true);
      try {
        const params = new URLSearchParams();
        if (customerId) params.set('customerId', customerId);
        await apiFetch(`/incidents/${encodeURIComponent(incident.id)}/attack-story/replay?${params.toString()}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId, incidentNumber: incident.incidentNumber, armIncidentName: incident.armIncidentName, graphIncidentId: incident.graphIncidentId, stepCount: replaySteps.length }),
        });
      } catch {
        // Replay is a client-side view over already-fetched story data. If audit logging fails, do not block the analyst.
      }
    }
  };

  const runAnalysis = async () => {
    if (!story) return;
    setAnalyzing(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (customerId) params.set('customerId', customerId);
      const data = await apiFetch<AttackStoryAiResult>(`/incidents/${encodeURIComponent(incident.id)}/attack-story/analyze?${params.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, story, extraPrompt: ATTACK_STORY_EXTRA_PROMPT }),
      });
      setAnalysis(data);
    } catch (e) {
      if (e instanceof ApiError && e.forbidden) setError('Your role does not have permission to run AI analysis on the attack story.');
      else setError(String(e instanceof Error ? e.message : e));
    } finally {
      setAnalyzing(false);
    }
  };

  const copyQuery = async (query: AttackStoryQuery) => {
    try {
      await navigator.clipboard.writeText(query.query);
      setCopiedQuery(query.title);
      setTimeout(() => setCopiedQuery(null), 1600);
    } catch {
      setCopiedQuery(null);
    }
  };

  if (loading) {
    return (
      <div className="mt-4 rounded-2xl border bg-card p-5 shadow-sm">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-56 rounded bg-muted" />
          <div className="h-28 rounded bg-muted" />
          <div className="h-4 w-2/3 rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error && !story) {
    return (
      <div className="mt-4 rounded-2xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-700 dark:text-red-300">
        <strong>Attack story unavailable:</strong> {error}
      </div>
    );
  }

  if (!story) return null;

  return (
    <section className="mt-4 space-y-4 rounded-2xl border bg-background/50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Attack Story / Investigation Graph</h3>
          <p className="text-sm text-muted-foreground">{story.story.summary}</p>
          <p className="mt-1 text-xs text-muted-foreground">Fetched: {formatDate(story.fetchedAt)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canReplay && replaySteps.length > 0 && (
            <button type="button" onClick={startReplay} className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-2 text-xs font-semibold text-fuchsia-700 hover:bg-fuchsia-500/15 dark:text-fuchsia-200">▶ Replay Attack</button>
          )}
          {story.portalUrls.sentinel && <a className="rounded-lg border px-3 py-2 text-xs hover:bg-muted" href={story.portalUrls.sentinel} target="_blank" rel="noreferrer">Open Sentinel</a>}
          {story.portalUrls.defender && <a className="rounded-lg border px-3 py-2 text-xs hover:bg-muted" href={story.portalUrls.defender} target="_blank" rel="noreferrer">Open Defender</a>}
        </div>
      </div>

      {error && <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200">{error}</div>}

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border bg-card p-3"><div className="text-xs text-muted-foreground">Alerts</div><div className="text-2xl font-semibold">{counts.alerts}</div></div>
        <div className="rounded-xl border bg-card p-3"><div className="text-xs text-muted-foreground">Entities</div><div className="text-2xl font-semibold">{counts.entities}</div></div>
        <div className="rounded-xl border bg-card p-3"><div className="text-xs text-muted-foreground">Relationships</div><div className="text-2xl font-semibold">{counts.edges}</div></div>
        <div className="rounded-xl border bg-card p-3"><div className="text-xs text-muted-foreground">Timeline Events</div><div className="text-2xl font-semibold">{counts.timeline}</div></div>
      </div>

      {replayOpen && canReplay && replaySteps.length > 0 && (
        <AttackReplayPanel
          steps={replaySteps}
          activeIndex={replayIndex}
          playing={replayPlaying}
          onPlayPause={() => setReplayPlaying(prev => !prev)}
          onNext={() => setReplayIndex(idx => Math.min(idx + 1, replaySteps.length - 1))}
          onPrev={() => setReplayIndex(idx => Math.max(idx - 1, 0))}
          onClose={() => { setReplayOpen(false); setReplayPlaying(false); }}
          onSelect={(idx) => { setReplayIndex(idx); setReplayPlaying(false); }}
        />
      )}

      <div className="rounded-2xl border bg-card p-4">
        <h4 className="mb-2 font-semibold text-foreground">Source Status</h4>
        <div className="grid gap-2 md:grid-cols-2">
          {story.sources.map(src => (
            <div key={src.source} className={`rounded-xl border p-3 text-sm ${src.ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
              <div className="font-medium">{src.ok ? '✅' : '⚠️'} {src.source}</div>
              <div className="mt-1 text-xs text-muted-foreground">{src.message}</div>
            </div>
          ))}
        </div>
      </div>

      <AttackGraphChart nodes={story.nodes} edges={story.edges} onAnalyze={runAnalysis} canAnalyze={canAnalyze} analyzing={analyzing} />

      <details className="rounded-xl border bg-card p-3 text-sm">
        <summary className="cursor-pointer font-medium">AI Analysis Prompt</summary>
        <p className="mt-2 whitespace-pre-wrap text-muted-foreground">{ATTACK_STORY_EXTRA_PROMPT}</p>
      </details>

      {analysis && (
        <div className="rounded-2xl border bg-card p-4 shadow-sm">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-semibold text-foreground">AI Attack Story Analysis</h4>
            <span className="rounded-full border px-2 py-1 text-xs text-muted-foreground">{analysis.usedAi ? `Model: ${analysis.model}` : 'Deterministic fallback'}</span>
          </div>
          <SimpleMarkdown text={analysis.analysis} />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border bg-card p-4">
          <h4 className="mb-3 font-semibold text-foreground">Attack Timeline</h4>
          {story.timeline.length === 0 ? (
            <p className="text-sm text-muted-foreground">No timestamped attack story was returned by Sentinel/Defender for this incident.</p>
          ) : (
            <div className="max-h-96 space-y-3 overflow-auto pr-1">
              {story.timeline.slice(0, 80).map(item => (
                <div key={item.id} className="border-l-2 border-primary/40 pl-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">{formatDate(item.timestamp)}</span>
                    {item.severity && <span className={`rounded-full border px-2 py-0.5 text-[11px] ${severityClass(item.severity)}`}>{item.severity}</span>}
                    <span className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground">{item.source}</span>
                  </div>
                  <div className="mt-1 font-medium text-foreground">{item.title}</div>
                  {item.detail && <div className="mt-1 text-xs text-muted-foreground">{item.detail}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border bg-card p-4">
          <h4 className="mb-3 font-semibold text-foreground">Story Gaps & Next Actions</h4>
          <div className="space-y-4 text-sm">
            <div>
              <div className="mb-1 font-medium">Gaps</div>
              {story.story.gaps.length ? story.story.gaps.map(g => <div key={g} className="mb-1 rounded-lg bg-amber-500/10 p-2 text-amber-800 dark:text-amber-200">{g}</div>) : <div className="text-muted-foreground">No major gaps detected from returned data.</div>}
            </div>
            <div>
              <div className="mb-1 font-medium">Recommended Actions</div>
              <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
                {story.story.nextActions.map(a => <li key={a}>{a}</li>)}
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4">
        <h4 className="mb-3 font-semibold text-foreground">Suggested KQL Pivots</h4>
        {story.suggestedKqlQueries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No KQL pivots could be generated because the graph has no linked alert/entity values yet.</p>
        ) : (
          <div className="space-y-3">
            {story.suggestedKqlQueries.map(query => (
              <div key={query.title} className="rounded-xl border bg-muted/20 p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-foreground">{query.title}</div>
                    <div className="text-xs text-muted-foreground">{query.description}</div>
                  </div>
                  <div className="flex gap-2">
                    {onRunKql && <button type="button" className="rounded-lg border px-3 py-1.5 text-xs hover:bg-muted" onClick={() => onRunKql(query.query)}>Open in Advanced Hunting</button>}
                    <button type="button" className="rounded-lg border px-3 py-1.5 text-xs hover:bg-muted" onClick={() => void copyQuery(query)}>{copiedQuery === query.title ? 'Copied' : 'Copy'}</button>
                  </div>
                </div>
                <pre className="mt-3 max-h-48 overflow-auto rounded-lg bg-background p-3 text-xs text-muted-foreground"><code>{query.query}</code></pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default AttackStoryPanel;
